import React from 'react';
import { Box } from '@mui/material';
import { SEARCE_COLORS } from '../../theme';

/**
 * Shared styles for displaying rich text HTML (from TipTap editor) in view modals
 * and read-only views. Preserves formatting: lists, tables, blockquote, bold,
 * italic, underline, text color, highlight, alignment.
 */
export const RICH_TEXT_VIEW_SX = {
  fontSize: '0.875rem',
  lineHeight: 1.6,
  color: SEARCE_COLORS.textSecondary,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  // Container padding so content does not touch borders
  padding: '16px 20px',
  '& p': {
    margin: '0 0 0.75em 0',
    paddingLeft: 0,
    '&:last-child': { marginBottom: 0 },
  },
  '& ul': {
    margin: '0.5em 0',
    paddingLeft: '24px',
    paddingRight: 0,
  },
  '& ol': {
    margin: '0.5em 0',
    paddingLeft: '24px',
    paddingRight: 0,
  },
  '& li': {
    marginBottom: '0.25em',
  },
  '& blockquote': {
    margin: '0.75em 0',
    paddingLeft: '16px',
    borderLeft: `4px solid ${SEARCE_COLORS.primary}`,
    color: SEARCE_COLORS.textSecondary,
  },
  '& h1, & h2, & h3, & h4': {
    margin: '0.75em 0 0.5em 0',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  '& h1': { fontSize: '1.25rem' },
  '& h2': { fontSize: '1.125rem' },
  '& h3': { fontSize: '1rem' },
  '& strong': { fontWeight: 600 },
  '& em': { fontStyle: 'italic' },
  '& u': { textDecoration: 'underline' },
  '& a': { color: SEARCE_COLORS.primary, textDecoration: 'none' },
  '& a:hover': { textDecoration: 'underline' },
  // Preserve inline styles from editor (color, highlight, text-align) - do not override span/mark
  '& span[style]': { fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' },
  '& mark': {
    padding: '0 2px',
    fontFamily: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    marginTop: '12px',
    marginBottom: '12px',
    border: '1px solid #d0d7de',
  },
  '& th, & td': {
    border: '1px solid #d0d7de',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '0.875rem',
    lineHeight: 1.5,
  },
  '& th': {
    backgroundColor: '#f8fafc',
    fontWeight: 600,
  },
  '& .tableWrapper': {
    margin: '12px 0',
    overflowX: 'auto',
  },
} as const;

interface RichTextViewProps {
  html: string;
  sx?: object;
}

/**
 * Read-only view for rich text HTML. Use in modals and view pages so formatting
 * (lists, tables, bold, italic, underline, highlight, color, alignment) matches
 * the editor and has proper left/right padding.
 */
const RichTextView: React.FC<RichTextViewProps> = ({ html, sx = {} }) => {
  if (!html) return null;
  return (
    <Box
      sx={{
        ...RICH_TEXT_VIEW_SX,
        ...sx,
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RichTextView;
